import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, ExternalLink, Check, X } from "lucide-react";
import memberApi from "../../lib/memberApi";
import { PortalNav } from "../../components/portal/PortalNav";

export default function DoneForYou() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await memberApi.get("/portal/settings");
        setSettings(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] noise-overlay text-white">
      <PortalNav />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <Link to="/portal" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors mb-8">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>

        {loading ? (
          <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-accent" size={28} /></div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent mb-3">// Optional · Done for you</p>
            <h1 data-testid="dfy-heading" className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl mb-6">
              {settings?.aura_headline || "Want this done for you?"}
            </h1>

            <p className="font-body text-white/65 leading-relaxed text-base mb-8">{settings?.aura_subhead}</p>

            <div className="border border-white/10 bg-[#0a0a0a] p-6 mb-8">
              <h2 className="font-display font-bold text-lg mb-4">Doing it yourself vs. a removal service</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 block mb-3">This pack (DIY)</span>
                  <ul className="space-y-2 font-body text-sm text-white/60">
                    <li className="flex gap-2"><Check size={16} className="text-emerald-400 shrink-0 mt-0.5" /> One-time payment, yours for life</li>
                    <li className="flex gap-2"><Check size={16} className="text-emerald-400 shrink-0 mt-0.5" /> No subscription, no data handed over</li>
                    <li className="flex gap-2"><Check size={16} className="text-emerald-400 shrink-0 mt-0.5" /> You control every step</li>
                    <li className="flex gap-2"><X size={16} className="text-white/30 shrink-0 mt-0.5" /> Takes your own time each quarter</li>
                  </ul>
                </div>
                <div>
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 block mb-3">A removal service (Aura)</span>
                  <ul className="space-y-2 font-body text-sm text-white/60">
                    <li className="flex gap-2"><Check size={16} className="text-emerald-400 shrink-0 mt-0.5" /> Automated broker removal + monitoring</li>
                    <li className="flex gap-2"><Check size={16} className="text-emerald-400 shrink-0 mt-0.5" /> Runs continuously in the background</li>
                    <li className="flex gap-2"><X size={16} className="text-white/30 shrink-0 mt-0.5" /> Monthly subscription fee</li>
                    <li className="flex gap-2"><X size={16} className="text-white/30 shrink-0 mt-0.5" /> You share some info with the provider</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border border-accent/40 bg-[#160606] p-6" data-testid="dfy-cta-box">
              <p className="font-body text-sm text-white/70 leading-relaxed mb-5">
                We recommend Aura if you'd rather have this handled for you. To be clear: we're not affiliated with or endorsed by them beyond recommending their service, and it's a subscription, the opposite of this pack's one-time, no-handover approach. Use it only if that trade-off fits you.
              </p>
              <a
                href={settings?.aura_url || "https://www.aura.com/"}
                target="_blank"
                rel="noopener noreferrer sponsored"
                data-testid="dfy-aura-link"
                className="inline-flex items-center gap-2 bg-accent text-white font-mono uppercase tracking-[0.15em] text-sm px-7 py-4 hover:bg-white hover:text-black transition-colors"
              >
                Check out Aura <ExternalLink size={15} />
              </a>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
